import { useState } from 'react';
import { usePaywall } from '@/hooks/usePaywall';
import { trackCustomEvent } from '@/lib/appInsights';
import { SubscriptionStatus as SubscriptionStatuses, TrackingCustomEvents, TrialStatus } from '@/constants/constants';
import Button from '@/components/elements/Button';
import Modal from '@/components/elements/Modal';
import type { Subscription } from '@/types/models.types';
import type { PaywallContextType } from '@/types/util.types';
import { useNavigate } from 'react-router-dom';

type CancelSubscriptionProps = {
  subscription: Subscription;
  className?: string;
};

function CancelSubscription({ subscription, className }: CancelSubscriptionProps) {
  const navigate = useNavigate();
  const { cancelSubscription } = usePaywall() as PaywallContextType;
  const [cancelInProgress, setCancelInProgress] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const { Cancelling, Expired } = SubscriptionStatuses;
  const { status, trialStatus, author, stripeSubscriptionId } = subscription;
  const shouldShowCancelButton = status !== Cancelling && status !== Expired;

  function openConfirmationModal() {
    setShowConfirmation(true);
  }

  function closeConfirmationModal() {
    setShowConfirmation(false);
  }

  function navigateToContactPage() {
    navigate('/contact');
  }

  async function handleCancelSubscription() {
    if (!subscription.author) return;

    setCancelInProgress(true);
    setShowConfirmation(false);

    try {
      const result = await cancelSubscription(stripeSubscriptionId);

      const eventType =
        trialStatus === TrialStatus.InTrial
          ? TrackingCustomEvents.click.purchases.cancel
          : TrackingCustomEvents.click.purchases.unsubscribe;

      trackCustomEvent(eventType, {
        subscription_id: subscription.stripeSubscriptionId,
        author_id: subscription.author.id,
        subscription_status: subscription.status,
      });

      if (result && result.success) {
        setCancelInProgress(false);
        return;
      }
    } catch (error) {
      console.error('Error cancelling subscription', error);
      alert('There was an error cancelling your subscription. Please try again later.');
    } finally {
      setCancelInProgress(false);
    }
  }

  if (!shouldShowCancelButton) {
    return null;
  }

  return subscription?.author?.id ? (
    <>
      <Button
        variant='faded'
        data-cy='cancel-subscription'
        disabled={cancelInProgress}
        className={`my-0 h-9 min-w-max ${className}`}
        onClick={openConfirmationModal}
      >
        Cancel {trialStatus === TrialStatus.InTrial ? 'Trial' : 'Subscription'}
      </Button>

      <Modal
        title='Cancel Subscription'
        isOpen={showConfirmation}
        onClose={closeConfirmationModal}
        buttons={[
          <Button
            key='close-modal'
            variant='hollow'
            buttonText='Keep Subscription'
            disabled={cancelInProgress}
            onClick={closeConfirmationModal}
          />,
          <Button
            key='cancel-subscription'
            data-cy='confirm-cancel'
            variant='gradient'
            buttonText={'Cancel Subscription'}
            disabled={cancelInProgress}
            onClick={handleCancelSubscription}
          />,
        ]}
      >
        <p>
          Are you sure you want to cancel your {trialStatus === TrialStatus.InTrial ? 'trial' : 'subscription'} with{' '}
          {author?.name}? You will lose access to their content at the end of your current billing period.
        </p>
      </Modal>
    </>
  ) : (
    <Button variant='faded' onClick={navigateToContactPage} className='h-9'>
      Contact to Cancel
    </Button>
  );
}

export default CancelSubscription;
